// LOOP WORKER INVITE CLAIM FUNCTION V91 — claim invite through Cloud Function transaction, no client Firestore writes.
// LOOP WORKER INVITE CLAIM TOKEN PROOF V90 — claim worker invite by signed-in user plus URL token hash proof; no email-token dependency.
// LOOP WORKER INVITE CLAIM DEBUG/RULES V89 — step-level debug, REST updateMask fix, and tolerant invite claim flow.
// LOOP WORKER INVITE CLAIM NATIVE SAFE V88 — claim worker invites with Firebase SDK or manual-auth REST fallback.
// LOOP WORKER INVITE DEEPLINK V87 — supports production HTTPS invite links and custom-scheme app fallback.
// src/pages/WorkerInvitePage.jsx
// LOOP WORKER INVITES — PHASE 3 SECURE TOKENS
// - New links use inviteId + token.
// - Firestore stores only tokenHash, never the plain token.
// - Tokens expire and are one-time use.
// - Legacy links with ?token=... are still handled as fallback.

import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { auth, db, firebaseConfigRuntime } from "../firebase";
import { getManualAuth, isManualAuthLikelyValid } from "../utils/manualAuth";
import { httpRequestJson } from "../utils/nativeHttp";

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

async function sha256Hex(value) {
  const text = String(value || "");
  if (!text) throw new Error("missing_token");
  if (typeof crypto === "undefined" || !crypto.subtle || typeof TextEncoder === "undefined") {
    throw new Error("secure_hash_unavailable");
  }
  const encoded = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest("SHA-256", encoded);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

function getManualIdentity() {
  try {
    const ma = getManualAuth();
    if (ma && isManualAuthLikelyValid(ma) && (ma.uid || ma.email)) {
      return {
        uid: ma.uid ? String(ma.uid) : "",
        email: normalizeEmail(ma.email || ""),
        displayName: ma.displayName || ma.name || "",
        source: ma.source || "manual-auth",
        idToken: ma.idToken || "",
      };
    }
  } catch (_) {}
  return null;
}

function invitePageUserFromAuth(currentUser) {
  if (currentUser && (currentUser.uid || currentUser.email)) {
    return {
      uid: currentUser.uid ? String(currentUser.uid) : "",
      email: normalizeEmail(currentUser.email || ""),
      displayName: currentUser.displayName || "",
      source: "firebase-auth",
    };
  }
  return getManualIdentity();
}

function dateFromAny(value) {
  try {
    if (!value) return null;
    if (value.toDate) return value.toDate();
    const d = value instanceof Date ? value : new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch (_) {
    return null;
  }
}

function inviteIsExpired(invite) {
  const expiresAt = dateFromAny(invite?.expiresAt);
  return Boolean(expiresAt && expiresAt.getTime() < Date.now());
}

function buildWorkerCalendarPath(businessId, employeeId, email) {
  const base = `/business/${encodeURIComponent(String(businessId || ""))}/calendar`;
  if (employeeId) return `${base}?worker=${encodeURIComponent(String(employeeId))}`;
  if (email) return `${base}?workerEmail=${encodeURIComponent(String(email))}`;
  return base;
}

function buildWorkerInviteAppUrl({ businessId, inviteId, token }) {
  const qs = new URLSearchParams({
    businessId: String(businessId || ""),
    inviteId: String(inviteId || ""),
    token: String(token || ""),
  });
  return `loopreservas://worker-invite?${qs.toString()}`;
}

function isLikelyMobileBrowser() {
  try {
    const ua = String(navigator?.userAgent || "");
    return /Android|iPhone|iPad|iPod/i.test(ua);
  } catch (_) {
    return false;
  }
}

function isNativeLoopRuntime() {
  try {
    const cap = typeof window !== "undefined" ? window.Capacitor : null;
    if (!cap) return false;
    if (typeof cap.isNativePlatform === "function") return cap.isNativePlatform();
    const platform = typeof cap.getPlatform === "function" ? cap.getPlatform() : cap.platform || "web";
    return platform === "ios" || platform === "android";
  } catch (_) {
    return false;
  }
}

function firestoreProjectId() {
  const candidates = [
    firebaseConfigRuntime?.projectId,
    db?.app?.options?.projectId,
    typeof window !== "undefined" ? window.__FIREBASE_CONFIG__?.projectId : "",
  ];
  return String(candidates.find(Boolean) || "").trim();
}

function firestoreRestBase() {
  const projectId = firestoreProjectId();
  if (!projectId) throw new Error("missing_firestore_project_id");
  return `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents`;
}

function cleanDocPath(path) {
  return String(path || "").replace(/^\/+/, "").replace(/\/+$/, "");
}

function toFsValue(value) {
  if (value === null || value === undefined) return { nullValue: null };
  if (value instanceof Date) return { timestampValue: value.toISOString() };

  const type = typeof value;
  if (type === "string") return { stringValue: value };
  if (type === "boolean") return { booleanValue: value };
  if (type === "number") {
    if (Number.isInteger(value)) return { integerValue: String(value) };
    return { doubleValue: value };
  }
  if (Array.isArray(value)) return { arrayValue: { values: value.map(toFsValue) } };
  if (type === "object") {
    const fields = {};
    Object.entries(value).forEach(([key, val]) => {
      fields[key] = toFsValue(val);
    });
    return { mapValue: { fields } };
  }
  return { stringValue: String(value) };
}

function fromFsValue(value) {
  if (!value || typeof value !== "object") return null;
  if (Object.prototype.hasOwnProperty.call(value, "nullValue")) return null;
  if (Object.prototype.hasOwnProperty.call(value, "stringValue")) return value.stringValue;
  if (Object.prototype.hasOwnProperty.call(value, "booleanValue")) return Boolean(value.booleanValue);
  if (Object.prototype.hasOwnProperty.call(value, "integerValue")) {
    const n = Number(value.integerValue);
    return Number.isNaN(n) ? value.integerValue : n;
  }
  if (Object.prototype.hasOwnProperty.call(value, "doubleValue")) return Number(value.doubleValue);
  if (Object.prototype.hasOwnProperty.call(value, "timestampValue")) return value.timestampValue;
  if (Object.prototype.hasOwnProperty.call(value, "arrayValue")) {
    const values = Array.isArray(value.arrayValue?.values) ? value.arrayValue.values : [];
    return values.map(fromFsValue);
  }
  if (Object.prototype.hasOwnProperty.call(value, "mapValue")) {
    const fields = value.mapValue?.fields || {};
    const out = {};
    Object.entries(fields).forEach(([key, val]) => {
      out[key] = fromFsValue(val);
    });
    return out;
  }
  return null;
}

function parseRestDoc(json) {
  if (!json || typeof json !== "object" || !json.name) return null;
  const parts = String(json.name).split("/").filter(Boolean);
  const id = parts.length ? parts[parts.length - 1] : "";
  const fields = json.fields || {};
  const out = { id };
  Object.entries(fields).forEach(([key, value]) => {
    out[key] = fromFsValue(value);
  });
  return out;
}

async function getInviteIdToken(user) {
  try {
    if (auth.currentUser && typeof auth.currentUser.getIdToken === "function") {
      return await auth.currentUser.getIdToken(false);
    }
  } catch (_) {}
  try {
    const ma = getManualAuth();
    if (ma && isManualAuthLikelyValid(ma) && ma.idToken) return ma.idToken;
  } catch (_) {}
  return user?.idToken || "";
}

function shouldUseRestInviteClient(user) {
  try {
    const ma = getManualAuth();
    const hasManualToken = Boolean(ma && isManualAuthLikelyValid(ma) && ma.idToken);
    if (!hasManualToken) return false;
    if (!auth.currentUser) return true;
    if (isNativeLoopRuntime() && user?.source === "manual-auth") return true;
  } catch (_) {}
  return false;
}

function inviteNowValue(useRestClient) {
  return useRestClient ? new Date() : serverTimestamp();
}

async function restGetInviteDoc(path, user) {
  const token = await getInviteIdToken(user);
  if (!token) throw new Error("missing_auth_token_for_invite_rest");
  const url = `${firestoreRestBase()}/${cleanDocPath(path)}`;
  const json = await httpRequestJson({
    url,
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });
  return parseRestDoc(json);
}

async function restPatchInviteDoc(path, payload, user) {
  const token = await getInviteIdToken(user);
  if (!token) throw new Error("missing_auth_token_for_invite_rest");
  const fields = {};
  const updateMask = [];
  Object.entries(payload || {}).forEach(([key, value]) => {
    fields[key] = toFsValue(value);
    updateMask.push(key);
  });

  // LOOP WORKER INVITE CLAIM DEBUG/RULES V89
  // Firestore REST expects updateMask.fieldPaths as repeated query params.
  // A comma-joined mask can be interpreted as one invalid field path, which makes
  // native/manual-auth claim attempts fail before the mirror docs are written.
  const maskQs = updateMask
    .map((field) => `updateMask.fieldPaths=${encodeURIComponent(field)}`)
    .join("&");
  const url = `${firestoreRestBase()}/${cleanDocPath(path)}${maskQs ? `?${maskQs}` : ""}`;
  const json = await httpRequestJson({
    url,
    method: "PATCH",
    headers: { Authorization: `Bearer ${token}` },
    data: { fields },
  });
  return parseRestDoc(json);
}

async function inviteGetDoc(path, ref, user, useRestClient) {
  if (useRestClient) return restGetInviteDoc(path, user);
  const snap = await getDoc(ref);
  return snap.exists() ? { id: snap.id, ...(snap.data() || {}) } : null;
}

async function inviteUpdateDoc(path, ref, payload, user, useRestClient) {
  if (useRestClient) return restPatchInviteDoc(path, payload, user);
  await updateDoc(ref, payload);
  return { ok: true };
}

async function inviteSetDoc(path, ref, payload, user, useRestClient) {
  if (useRestClient) return restPatchInviteDoc(path, payload, user);
  await setDoc(ref, payload, { merge: true });
  return { ok: true };
}

function buildWorkerAccessPayload({ businessId, user, inviteData, inviteDocId }) {
  const employeeId = inviteData.employeeId || inviteData.workerId || null;
  const inviteEmail = normalizeEmail(inviteData.inviteEmail || inviteData.email || "");
  const authEmail = normalizeEmail(user.email || "");
  return {
    active: true,
    businessId,
    uid: user.uid,
    userUid: user.uid,
    authUid: user.uid,
    linkedUserId: user.uid,
    linkedUserUid: user.uid,
    email: authEmail || inviteEmail || null,
    authEmail: authEmail || null,
    authEmailLower: authEmail || null,
    inviteEmail: inviteEmail || null,
    inviteEmailLower: inviteEmail || null,
    employeeId,
    employeeName: inviteData.employeeName || inviteData.name || null,
    workerName: inviteData.employeeName || inviteData.name || null,
    name: inviteData.employeeName || inviteData.name || null,
    accessRole: inviteData.accessRole || "worker",
    loopRole: inviteData.accessRole || "worker",
    securityRole: inviteData.accessRole || "worker",
    permissions: inviteData.permissions || {},
    workerPermissions: inviteData.workerPermissions || inviteData.permissions || {},
    inviteId: inviteDocId,
    inviteToken: inviteDocId,
    inviteTokenVersion: inviteData.tokenVersion || (inviteData.tokenHash ? 2 : 1),
  };
}

function setInviteDebug(payload = {}) {
  try {
    const previous = window.__LOOP_WORKER_INVITE_DEBUG__ || {};
    const history = Array.isArray(previous.history) ? previous.history.slice(-18) : [];
    const entry = {
      stage: payload.stage || previous.stage || "unknown",
      step: payload.step || payload.stage || previous.step || "unknown",
      error: payload.error || null,
      at: new Date().toISOString(),
    };
    window.__LOOP_WORKER_INVITE_DEBUG__ = {
      ...previous,
      ...payload,
      history: [...history, entry],
      updatedAt: new Date().toISOString(),
    };
  } catch (_) {}
}

async function ensureWorkerAccessMirrors({ businessId, user, inviteData, inviteDocId, useRestClient }) {
  const workerAccessPayload = buildWorkerAccessPayload({ businessId, user, inviteData, inviteDocId });
  const now = inviteNowValue(useRestClient);
  const authEmail = normalizeEmail(user.email || "");
  const inviteEmail = normalizeEmail(inviteData.inviteEmail || inviteData.email || "");
  const employeeId = inviteData.employeeId || inviteData.workerId || null;
  const accessEmails = Array.from(new Set([inviteEmail, authEmail].filter(Boolean)));

  setInviteDebug({ stage: "writing_worker_access_uid", step: "workerAccess_uid", uid: user.uid, employeeId: employeeId || null });
  await inviteSetDoc(
    `businesses/${businessId}/workerAccess/${user.uid}`,
    doc(db, `businesses/${businessId}/workerAccess`, user.uid),
    { ...workerAccessPayload, linkedAt: now, updatedAt: now },
    user,
    useRestClient
  );

  if (authEmail) {
    setInviteDebug({ stage: "writing_worker_access_email", step: "workerAccessEmails", email: authEmail, employeeId: employeeId || null });
    await inviteSetDoc(
      `businesses/${businessId}/workerAccessEmails/${authEmail}`,
      doc(db, `businesses/${businessId}/workerAccessEmails`, authEmail),
      {
        ...workerAccessPayload,
        email: authEmail,
        employeeEmail: authEmail,
        updatedAt: now,
      },
      user,
      useRestClient
    );
  }

  if (employeeId) {
    try {
      setInviteDebug({ stage: "updating_employee_link", step: "employee_update", employeeId });
      await inviteUpdateDoc(
        `businesses/${businessId}/employees/${employeeId}`,
        doc(db, `businesses/${businessId}/employees`, employeeId),
        {
          userUid: user.uid,
          authUid: user.uid,
          linkedUserId: user.uid,
          linkedUserUid: user.uid,
          authEmail: authEmail || null,
          authEmailLower: authEmail || null,
          inviteEmail: inviteEmail || null,
          inviteEmailLower: inviteEmail || null,
          activeInviteId: inviteDocId,
          inviteId: inviteDocId,
          activeInviteToken: null,
          inviteToken: null,
          accessInviteToken: null,
          inviteTokenVersion: inviteData.tokenVersion || (inviteData.tokenHash ? 2 : 1),
          accessStatus: "linked",
          inviteStatus: "linked",
          linkedAt: now,
          inviteConsumedAt: now,
          accessEmails,
          allowedEmails: accessEmails,
          updatedAt: now,
        },
        user,
        useRestClient
      );
    } catch (employeeErr) {
      console.warn("[WorkerInvite] employee link mirror skipped", employeeErr);
      setInviteDebug({ employeeMirrorError: String(employeeErr?.message || employeeErr) });
    }
  }

  return { employeeId, authEmail, inviteEmail };
}


function cloudFunctionsBaseUrl() {
  const envBase = String(
    (typeof process !== "undefined" && process.env && (
      process.env.REACT_APP_FUNCTIONS_BASE_URL ||
      process.env.REACT_APP_CLOUD_FUNCTIONS_BASE_URL ||
      process.env.REACT_APP_FIREBASE_FUNCTIONS_BASE_URL
    )) || ""
  ).trim().replace(/\/+$/, "");
  if (envBase) return envBase;
  const projectId = firestoreProjectId();
  if (!projectId) throw new Error("missing_functions_project_id");
  return `https://us-central1-${projectId}.cloudfunctions.net`;
}

function claimWorkerInviteFunctionUrl() {
  return `${cloudFunctionsBaseUrl()}/claimWorkerInviteHttp`;
}

async function claimWorkerInviteViaFunction({ businessId, inviteId, token, user }) {
  const idToken = await getInviteIdToken(user);
  if (!idToken) throw new Error("missing_auth_token_for_invite_function");

  setInviteDebug({
    stage: "claiming_invite_function",
    step: "claimWorkerInviteHttp",
    functionUrl: claimWorkerInviteFunctionUrl(),
  });

  const json = await httpRequestJson({
    url: claimWorkerInviteFunctionUrl(),
    method: "POST",
    headers: {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    },
    data: {
      businessId: String(businessId || ""),
      inviteId: String(inviteId || ""),
      token: String(token || ""),
    },
  });

  if (!json || json.ok !== true) {
    const code = json?.code || json?.error || "claim_worker_invite_failed";
    const err = new Error(code);
    err.code = code;
    err.details = json || null;
    throw err;
  }

  setInviteDebug({
    stage: "claim_function_done",
    step: "claimWorkerInviteHttp_done",
    employeeId: json.employeeId || null,
    authEmail: json.authEmail || null,
  });

  return json;
}

function InviteCard({ children }) {
  return (
    <main style={{ minHeight: "70vh", display: "grid", placeItems: "center", padding: 24, background: "#f8fafc" }}>
      <section style={{ width: "min(560px, 100%)", background: "#fff", border: "1px solid #dbeafe", borderRadius: 24, boxShadow: "0 24px 70px rgba(15,23,42,.10)", overflow: "hidden" }}>
        <div style={{ padding: "22px 24px", borderBottom: "1px solid #e2e8f0" }}>
          <div style={{ color: "#0f766e", fontWeight: 900, letterSpacing: ".08em", fontSize: 12, textTransform: "uppercase" }}>Loop Reservas</div>
          <h1 style={{ margin: "8px 0 0", fontSize: 26, lineHeight: 1.1, color: "#0f172a" }}>Invitación de trabajador</h1>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </section>
    </main>
  );
}

export default function WorkerInvitePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(location.search || ""), [location.search]);
  const businessId = params.get("businessId") || params.get("biz") || "";
  const inviteId = params.get("inviteId") || params.get("id") || "";
  const token = params.get("token") || params.get("invite") || "";
  const inviteDocId = inviteId || token;

  const [authReady, setAuthReady] = useState(false);
  const [identity, setIdentity] = useState(() => invitePageUserFromAuth(auth.currentUser));
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [invite, setInvite] = useState(null);

  useEffect(() => {
    let mounted = true;
    const unsub = onAuthStateChanged(auth, (user) => {
      if (!mounted) return;
      setIdentity(invitePageUserFromAuth(user));
      setAuthReady(true);
    });
    const t = setTimeout(() => {
      if (!mounted) return;
      setIdentity((prev) => prev || getManualIdentity());
      setAuthReady(true);
    }, 1200);
    return () => {
      mounted = false;
      clearTimeout(t);
      try { unsub(); } catch (_) {}
    };
  }, []);

  const claimInvite = async () => {
    if (!businessId || !inviteDocId || !token) {
      setStatus("error");
      setMessage("El link de invitación está incompleto. Pide al dueño que lo copie nuevamente.");
      return;
    }
    const user = invitePageUserFromAuth(auth.currentUser) || identity || getManualIdentity();
    if (!user || !user.uid) {
      setStatus("login_required");
      setMessage("Inicia sesión con Apple, Google o email para vincular esta invitación.");
      return;
    }

    const useRestClient = shouldUseRestInviteClient(user);
    const invitePath = `businesses/${businessId}/workerInvites/${inviteDocId}`;
    const inviteRef = doc(db, `businesses/${businessId}/workerInvites`, inviteDocId);
    const now = () => inviteNowValue(useRestClient);

    setInviteDebug({
      stage: "start",
      businessId,
      inviteDocId,
      uid: user.uid,
      email: user.email || "",
      source: user.source || "unknown",
      useRestClient,
      native: isNativeLoopRuntime(),
    });

    setStatus("loading");
    setMessage("Validando invitación segura...");
    try {
      setMessage("Vinculando tu cuenta de trabajador...");
      setInviteDebug({ stage: "claiming_invite", step: "claimWorkerInviteHttp" });
      const ensured = await claimWorkerInviteViaFunction({ businessId, inviteId: inviteDocId, token, user });
      setInviteDebug({ stage: "mirrors_written", step: "claimWorkerInviteHttp_done", employeeId: ensured.employeeId || null });

      setStatus("success");
      setMessage("Cuenta vinculada correctamente. Este link ya quedó consumido. Redirigiendo a tu agenda...");
      setTimeout(() => navigate(buildWorkerCalendarPath(businessId, ensured.employeeId, ensured.authEmail || ensured.inviteEmail), { replace: true }), 900);
    } catch (err) {
      console.error("[WorkerInvite] claim failed", err);
      setInviteDebug({
        stage: "error",
        step: (window.__LOOP_WORKER_INVITE_DEBUG__ || {}).step || "unknown",
        error: String(err?.message || err?.code || err || ""),
        errorCode: String(err?.code || ""),
        errorName: String(err?.name || ""),
        useRestClient: Boolean((window.__LOOP_WORKER_INVITE_DEBUG__ || {}).useRestClient),
      });
      setStatus("error");
      const code = String(err?.message || err?.code || err || "");
      if (code.includes("permission") || code.includes("403") || code.includes("PERMISSION_DENIED")) {
        setMessage("No pudimos vincular por permisos. Verifica que la función V91 esté desplegada y que estés iniciado con la cuenta del trabajador.");
      } else if (code.includes("missing_auth_token") || code.includes("unauthenticated")) setMessage("Tu sesión no entregó token de autenticación. Cierra sesión, entra de nuevo y vuelve a abrir el link.");
      else if (code.includes("invite_not_found")) setMessage("No encontramos esta invitación. Pide al dueño que genere un link nuevo.");
      else if (code.includes("invalid_token")) setMessage("El token de esta invitación no coincide. Pide al dueño que copie un link nuevo.");
      else if (code.includes("invite_expired")) setMessage("Esta invitación expiró. Pide al dueño que genere una nueva.");
      else if (code.includes("invite_used")) setMessage("Esta invitación ya fue usada. Pide al dueño que restablezca el acceso si necesitas vincular otra cuenta.");
      else if (code.includes("invite_disabled")) setMessage("Esta invitación está desactivada. Pide al dueño que genere una nueva.");
      else setMessage("No pudimos vincular esta invitación. Revisa la consola o pide un link nuevo.");
    }
  };

  useEffect(() => {
    if (!authReady) return;
    if (!identity || !identity.uid) {
      setStatus("login_required");
      setMessage("Inicia sesión con Apple, Google o email para vincular esta invitación.");
      return;
    }
    if (status === 'idle' || status === 'login_required') {
      setStatus('idle');
      setMessage('Confirma que quieres conectar esta cuenta al equipo. Recibirás los permisos que el negocio haya configurado.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authReady, identity?.uid]);

  const loginPath = `/login?redirect=${encodeURIComponent(`${location.pathname}${location.search || ""}`)}`;
  const expiresAt = dateFromAny(invite?.expiresAt);
  const appInviteUrl = buildWorkerInviteAppUrl({ businessId, inviteId: inviteDocId, token });
  const showOpenAppFallback = Boolean(!isNativeLoopRuntime() && isLikelyMobileBrowser() && businessId && inviteDocId && token);

  return (
    <InviteCard>
      <div style={{ display: "grid", gap: 14 }}>
        <p style={{ margin: 0, color: "#475569", lineHeight: 1.55 }}>
          Este link vincula tu cuenta real al equipo del negocio. Puedes iniciar sesión con Apple, Google o email; Loop guardará tu acceso internamente sin mostrar datos técnicos al negocio.
        </p>

        {invite?.employeeName ? (
          <div style={{ padding: 12, borderRadius: 16, background: "#f0fdfa", border: "1px solid #99f6e4" }}>
            <strong style={{ color: "#0f766e" }}>{invite.employeeName}</strong>
            {invite.inviteEmail ? <div style={{ color: "#64748b", fontSize: 13 }}>{invite.inviteEmail}</div> : null}
            {expiresAt ? <div style={{ color: "#64748b", fontSize: 12, marginTop: 4 }}>Expira: {expiresAt.toLocaleDateString("es-PA", { day: "2-digit", month: "short", year: "numeric" })}</div> : null}
          </div>
        ) : null}

        {showOpenAppFallback ? (
          <button
            type="button"
            onClick={() => { try { window.location.href = appInviteUrl; } catch (_) {} }}
            style={{ border: "1px solid #0f766e", borderRadius: 16, padding: "12px 16px", background: "#ffffff", color: "#0f766e", fontWeight: 900 }}
          >
            Abrir en la app Loop
          </button>
        ) : null}

        <div style={{ padding: 14, borderRadius: 18, background: status === "error" ? "#fff7ed" : status === "success" ? "#ecfdf5" : "#f8fafc", border: "1px solid #e2e8f0", color: "#0f172a" }}>
          <strong>{status === "loading" ? "Vinculando..." : status === "success" ? "Listo" : status === "login_required" ? "Falta iniciar sesión" : status === "error" ? "No se pudo vincular" : "Preparando invitación"}</strong>
          <div style={{ marginTop: 4, color: "#475569" }}>{message || "Un momento..."}</div>
        </div>

        {status === "login_required" ? (
          <button type="button" onClick={() => navigate(loginPath)} style={{ border: 0, borderRadius: 16, padding: "13px 16px", background: "#00675f", color: "#fff", fontWeight: 900 }}>
            Iniciar sesión y vincular
          </button>
        ) : null}

        {status === 'idle' && identity?.uid ? <button type="button" onClick={claimInvite} style={{border: 0, borderRadius: 10, padding: 14, background: '#00675f', color: '#fff', fontWeight: 700}}>Aceptar invitación con {identity.email || 'mi cuenta'}</button> : null}
        {status === "error" ? (
          <button type="button" onClick={claimInvite} style={{ border: "1px solid #0f766e", borderRadius: 16, padding: "12px 16px", background: "#fff", color: "#0f766e", fontWeight: 900 }}>
            Intentar otra vez
          </button>
        ) : null}
      </div>
    </InviteCard>
  );
}
